import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FlexBox, JoonUIColor, SPACING, Typography } from "@joonapp/web-shared"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import useAIQuest from "./hooks/useAIQuest"
import useAIQuestStore from "./hooks/useAIQuestStore"
import AnimatedStars from "../../../components/animatedStars/AnimatedStars"
import CustomTextArea from "../../../components/textArea/CustomTextArea"

enum SuggestionType {
  CREATE = "create",
  EDIT = "edit",
}

const PromptTextArea = ({
  placeholder,
  showSuggestions = true,
}: {
  placeholder?: string
  showSuggestions?: boolean
}) => {
  const { prompt, setPrompt } = useAIQuestStore()
  const [suggestionType, setSuggestionType] = useState<SuggestionType | null>(
    null
  )
  const { generateResponse, initializeChatSession } = useAIQuest()
  const { t } = useTranslation()
  const resolvedPlaceholder = placeholder ?? t("JOONIPER_PROMPT_PLACEHOLDER")

  const handleShareFeedback = () => {
    window.open(
      "https://forms.gle/ZcCXH7z6Li6HKtTE9",
      "_blank",
      "noopener,noreferrer"
    )
  }

  return (
    <FlexBox
      direction="column"
      gap={SPACING.space2}
      style={{ zIndex: 1, position: "relative" }}
    >
      {showSuggestions && (
        <div style={{ width: "100%" }}>
          {suggestionType ? (
            <QuestPromptSuggestions
              type={suggestionType}
              selectSuggestion={(suggestion) => {
                initializeChatSession()
                setPrompt(suggestion)
                setSuggestionType(null)
              }}
            />
          ) : (
            prompt.length === 0 && (
              <FlexBox direction="column" gap={SPACING.space2}>
                <SuggestionTypeButton
                  text={t("JOONIPER_CREATE_NEW")}
                  onClick={() => setSuggestionType(SuggestionType.CREATE)}
                />
                <SuggestionTypeButton
                  text={t("JOONIPER_MODIFY_EXISTING")}
                  onClick={() => setSuggestionType(SuggestionType.EDIT)}
                />
              </FlexBox>
            )
          )}
        </div>
      )}

      <form
        onSubmit={generateResponse}
        style={{ width: "100%", background: "white" }}
      >
        <FlexBox
          direction="row"
          align="center"
          wrap={false}
          className="customized-message-input"
          gap={SPACING.space1}
          style={{
            border: `1px solid ${JoonUIColor.border.default}`,
            borderRadius: SPACING.space2,
          }}
        >
          <CustomTextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={255}
            placeholder={resolvedPlaceholder}
            style={{
              maxHeight: "200px",
              fontSize: "16px",
              overflow: "auto",
              border: "none",
            }}
            hideBorder
            defaultRows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                generateResponse(e)
              }
            }}
          />
          {prompt.length > 0 && <SendMessageButton />}
        </FlexBox>
      </form>
      <FlexBox direction="column">
        <Typography
          variant="bodyXSmall"
          textAlign="left"
          color={JoonUIColor.text.secondary}
        >
          {t("JOONIPER_BETA")}
        </Typography>
        <button onClick={handleShareFeedback}>
          <Typography
            variant="bodyXSmallBold"
            textAlign="left"
            color={JoonUIColor.text.primaryAccent}
          >
            {t("JOONIPER_SHARE_FEEDBACK")}
          </Typography>
        </button>
      </FlexBox>
    </FlexBox>
  )
}

export default PromptTextArea

const SendMessageButton = () => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <button
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: SPACING.space5,
        marginTop: "auto",
        marginBottom: "auto",
        marginLeft: "auto",
        marginRight: SPACING.space1,
        height: "100%",
        width: SPACING.space8,
        borderRadius: SPACING.space2,
        background: isHovered ? JoonUIColor.background.xlightGray : "white",
      }}
      type="submit"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FontAwesomeIcon
        icon={faArrowUp}
        color={JoonUIColor.icon.neutral}
        fontSize={16}
      />
    </button>
  )
}

const QuestPromptSuggestions = ({
  type,
  selectSuggestion,
}: {
  type: SuggestionType
  selectSuggestion: (suggestion: string) => void
}) => {
  const { t } = useTranslation()
  const suggestionKeys = getRandomPrompts(
    type === "create" ? createSuggestionKeys : editSuggestionKeys,
    4
  )

  return (
    <FlexBox
      direction="column"
      style={{
        position: "absolute",
        bottom: "100%",
        background: "white",
        borderRadius: SPACING.space2,
      }}
    >
      <Typography
        variant="bodySmall"
        textAlign="left"
        color={JoonUIColor.text.secondary}
        style={{ marginBottom: SPACING.space1 }}
      >
        {t("JOONIPER_STARTER_PROMPTS")}
      </Typography>
      {suggestionKeys.map((suggestionKey) => (
        <button
          key={suggestionKey}
          style={{
            padding: SPACING.space2,
            width: "100%",
            background: JoonUIColor.background.lightGray,
            borderRadius: SPACING.space2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: SPACING.space2,
            gap: SPACING.space2,
          }}
          onClick={() => selectSuggestion(t(suggestionKey))}
        >
          <Typography variant="bodySmall" textAlign="left">
            {t(suggestionKey)}
          </Typography>
          <FontAwesomeIcon
            icon={faArrowDown}
            color={JoonUIColor.icon.neutral}
          />
        </button>
      ))}
    </FlexBox>
  )
}

const SuggestionTypeButton = ({
  text,
  onClick,
}: {
  text: string
  onClick: () => void
}) => {
  return (
    <button
      style={{
        padding: SPACING.space1,
        border: `1px solid ${JoonUIColor.border.default}`,
        borderRadius: SPACING.space2,
        display: "flex",
        alignItems: "flex-end",
        gap: SPACING.space2,
        background: "white",
      }}
      onClick={onClick}
    >
      <AnimatedStars
        animate={false}
        color={JoonUIColor.semantic.primary}
        scale={0.8}
      />
      <Typography variant="bodySmall" textAlign="left">
        {text}
      </Typography>
    </button>
  )
}

const getRandomPrompts = <T,>(bank: readonly T[], numPrompts: number): T[] => {
  const shuffledBank = [...bank].sort(() => Math.random() - 0.5)
  return shuffledBank.slice(0, numPrompts)
}

const createSuggestionKeys = [
  "JOONIPER_CREATE_SUGGESTION_1",
  "JOONIPER_CREATE_SUGGESTION_2",
  "JOONIPER_CREATE_SUGGESTION_3",
  "JOONIPER_CREATE_SUGGESTION_4",
  "JOONIPER_CREATE_SUGGESTION_5",
  "JOONIPER_CREATE_SUGGESTION_6",
  "JOONIPER_CREATE_SUGGESTION_7",
  "JOONIPER_CREATE_SUGGESTION_8",
  "JOONIPER_CREATE_SUGGESTION_9",
  "JOONIPER_CREATE_SUGGESTION_10",
  "JOONIPER_CREATE_SUGGESTION_11",
  "JOONIPER_CREATE_SUGGESTION_12",
  "JOONIPER_CREATE_SUGGESTION_13",
  "JOONIPER_CREATE_SUGGESTION_14",
  "JOONIPER_CREATE_SUGGESTION_15",
] as const

const editSuggestionKeys = [
  "JOONIPER_EDIT_SUGGESTION_1",
  "JOONIPER_EDIT_SUGGESTION_2",
  "JOONIPER_EDIT_SUGGESTION_3",
  "JOONIPER_EDIT_SUGGESTION_4",
  "JOONIPER_EDIT_SUGGESTION_5",
  "JOONIPER_EDIT_SUGGESTION_6",
  "JOONIPER_EDIT_SUGGESTION_7",
  "JOONIPER_EDIT_SUGGESTION_8",
  "JOONIPER_EDIT_SUGGESTION_9",
  "JOONIPER_EDIT_SUGGESTION_10",
  "JOONIPER_EDIT_SUGGESTION_11",
  "JOONIPER_EDIT_SUGGESTION_12",
  "JOONIPER_EDIT_SUGGESTION_13",
  "JOONIPER_EDIT_SUGGESTION_14",
] as const
